<script lang="ts">
	import Icon from '../icon/Icon.svelte'
	import { ButtonType, ButtonVariant } from './index'
	import { _ } from '@repo/i18n'
	import type { IconSource } from '@steeze-ui/heroicons'

	interface Props {
		label?: string
		loading?: boolean
		disabled?: boolean | null
		small?: boolean
		xSmall?: boolean
		full?: boolean
		contentWidth?: boolean
		loadingText?: string
		type?: ButtonType
		variant?: ButtonVariant
		icon?: IconSource
		additionalButtonClasses?: string | null
		backgroundColor?: string | null
		href?: string | null
		target?: string | null
		name: string
		form?: string
		groupedRight?: boolean
		groupedLeft?: boolean
		iconAfter?: boolean
		stopPropagation?: boolean
		hideLabel?: boolean
		onclick?: (event: Event) => void
		iconSlot?: import('svelte').Snippet
		children?: import('svelte').Snippet
	}

	let {
		label = '',
		loading = false,
		disabled = null,
		small = false,
		xSmall = false,
		full = false,
		contentWidth = false,
		loadingText = '',
		type = ButtonType.button,
		variant = ButtonVariant.default,
		icon = null,
		additionalButtonClasses = null,
		backgroundColor = null,
		href = null,
		target = null,
		name,
		form = null,
		groupedRight = false,
		groupedLeft = false,
		iconAfter = false,
		stopPropagation = false,
		onclick = null,
		iconSlot,
		children,
		hideLabel = false,
	}: Props = $props()

	let tagName: 'a' | 'button' = $derived(href ? 'a' : 'button')

	function getButtonColorClass(variant: ButtonVariant, disabled: boolean, loading: boolean) {
		if (variant === ButtonVariant.link) {
			return `
        bg-transparent hover:bg-transparent dark:hover:bg-transparent text-base border border-transparent
        ${disabled ? 'text-gray-300 dark:text-gray-600' : 'text-base cursor-pointer underline hover:no-underline '}
      `
		}

		if (loading || disabled) {
			return 'bg-white border border-gray-300 text-gray-300 dark:bg-transparent dark:text-gray-600 dark:border-gray-600'
		}

		switch (variant) {
			case ButtonVariant.green:
				return 'bg-green-400 hover:bg-green-700 hover:shadow-green-700/15 text-white border border-transparent'
			case ButtonVariant.default:
				return 'bg-primary-500 hover:bg-primary-700 hover:shadow-blue-700/15 text-white border border-transparent'
			case ButtonVariant.warning:
				return 'bg-yellow-500 hover:bg-yellow-600 hover:shadow-yellow-700/15 text-dark border border-transparent'
			case ButtonVariant.impersonate:
				return 'bg-purple-500 hover:bg-purple-600 hover:shadow-purple-700/15 text-white border border-transparent'
			case ButtonVariant.error:
				return 'bg-red-500 hover:bg-red-600 hover:shadow-red-700/15 text-white border border-transparent'
			case ButtonVariant.inverse:
				return 'bg-transparent hover:bg-primary-50 hover:shadow-blue-700/15 border border-primary-500 text-primary-500 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900'
			case ButtonVariant.inverseGreen:
				return 'bg-transparent hover:bg-green-50 hover:shadow-green-700/15 border border-green-400 text-green-400 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/15'
			case ButtonVariant.inverseRed:
				return 'bg-transparent hover:bg-red-50 hover:shadow-red-700/15 border border-red-500 text-red-500 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/15'
			case ButtonVariant.inverseImpersonate:
				return 'bg-white hover:bg-purple-50 hover:shadow-purple-700/15 border border-purple-400 text-purple-400 dark:border-purple-400 dark:text-purple-400 dark:hover:bg-purple-900/15'
			case ButtonVariant.monochrome:
				return 'bg-white dark:bg-gray-900 hover:bg-gray-100 hover:shadow-transparent dark:hover:bg-gray-800 text-base text-gray-900 dark:text-gray-200 border border-gray-200 dark:border-gray-500'
			case ButtonVariant.black:
				return 'bg-dark dark:bg-gray-300 hover:bg-black hover:shadow-transparent dark:hover:bg-gray-400 text-base text-white dark:text-black border border-transparent'
		}
	}

	function handleClick(event: Event) {
		if (stopPropagation) {
			event.stopPropagation()
		}
		if (onclick) {
			onclick(event)
		}
	}

	let restProps = $derived(
		tagName === 'button'
			? {
					disabled: disabled || loading || null,
					type: ButtonType[type],
					form,
					name,
				}
			: {
					href,
					target,
				},
	)
</script>

<svelte:element
	this={tagName}
	style={backgroundColor}
	class="
    inline-block text-center whitespace-nowrap transition-all
    {!disabled && variant !== ButtonVariant.link ? 'hover:shadow-lg active:translate-y-0.5' : ''}
    {groupedRight ? '' : 'rounded-r-lg'}
    {groupedLeft ? '' : 'rounded-l-lg'}
    {additionalButtonClasses}
    {getButtonColorClass(variant, disabled, loading)}
    {!full && !contentWidth ? 'md:max-w-64' : ''}
    {small
		? 'w-auto max-w-3xs px-4 py-2 text-base'
		: xSmall
			? 'w-auto max-w-3xs px-2 py-1 text-sm'
			: 'px-6 py-3 font-bold lg:text-base'}
    {!contentWidth && !small && !xSmall ? 'w-full' : ''}
    {loading || disabled ? 'pointer-events-none' : ''}
  "
	data-test={name ? `button-${name}` : null}
	onclick={handleClick}
	{...restProps}
	aria-label={hideLabel ? label : null}
>
	<div class="flex items-center justify-center gap-2">
		{#if (icon || iconSlot) && !iconAfter}
			{#if iconSlot}
				{@render iconSlot()}
			{:else}
				<Icon small src={icon} />
			{/if}
		{/if}

		{#if (children || label) && !hideLabel}
			<div>
				{#if loading}
					{#if loadingText}
						{loadingText}
					{:else}
						{$_('button.sending')}
					{/if}
				{:else if children}{@render children()}{:else}
					{label}
				{/if}
			</div>
		{/if}

		{#if (icon || iconSlot) && iconAfter}
			{#if iconSlot}
				{@render iconSlot()}
			{:else}
				<Icon small src={icon} />
			{/if}
		{/if}
	</div>
</svelte:element>
